import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import ReactModal from 'react-modal'
import Router from 'next/router'
import Typo from 'constants/typography'
import ResponsiveImage from 'components/images/responsive-image'
import useIntersection from 'components/custom-hooks/useIntersection'
import ShopTheLook from '../shop-the-look'
import ProductVirtualTryOn from '../../product-virtual-try-on/ProductVirtualTryOn'
import segmentProductImageGalleryViewed from '../carousel-images-segment/carouselImagesSegment'
import ZoomImage from './ZoomImage'
import MediaVideo from './MediaVideo'
import { clampIndex } from './media'
import copy from './copy'
import styles from './styles.scss'

const ProductGallery = ({
  id,
  media,
  mobile,
  t,
  language,
  isShownShopTheLook,
  shopLookCtaEventTracking,
  virtual_try_on_enabled,
  isOpenVirtualTryOn,
}) => {
  const [index, setIndex] = useState(0)
  const [open, setOpen] = useState(false)
  const root = useRef(null)
  const rail = useRef(null)
  const thumbnails = useRef(null)
  const closeButton = useRef(null)
  const tracked = useRef(null)
  const drag = useRef(null)
  const ignoreClick = useRef(false)
  const animationFrame = useRef(null)
  const visible = useIntersection(root, { threshold: 0.2, once: false })
  const activeIndex = clampIndex(index, media.length)
  const activeIndexRef = useRef(activeIndex)
  activeIndexRef.current = activeIndex
  const viewerThumbnails = useRef(null)
  const active = media[activeIndex]
  const labels = (key) => copy(t, language, key)
  const label = (item, i) => `${labels(item.src ? 'video' : 'photo')} ${i + 1}`
  const appElement =
    typeof document !== 'undefined' ? document.getElementById('__next') : null

  const select = (next) => {
    const selected = clampIndex(next, media.length)
    setIndex(selected)
    if (rail.current)
      rail.current.scrollLeft = selected * rail.current.clientWidth
  }
  useEffect(() => {
    if ((visible || open) && active && tracked.current !== active.key) {
      tracked.current = active.key
      segmentProductImageGalleryViewed({
        ref_id: id,
        ref_type: 'pdp',
        id_product: id,
        image_type: active.imageType,
      })
    }
  }, [active, id, visible, open])
  useEffect(() => {
    const node = rail.current
    const resize = () => {
      if (node) node.scrollLeft = activeIndexRef.current * node.clientWidth
    }
    window.addEventListener('resize', resize)
    resize()
    return () => window.removeEventListener('resize', resize)
  }, [mobile])
  useEffect(() => {
    const list = open ? viewerThumbnails.current : thumbnails.current
    const item = list?.children[activeIndex]
    if (!item) return
    // Scroll the thumbnail strip only; never scroll the product page.
    const bounds = item.getBoundingClientRect()
    const container = list.getBoundingClientRect()
    if (mobile || open) {
      if (bounds.left < container.left)
        list.scrollLeft -= container.left - bounds.left
      else if (bounds.right > container.right)
        list.scrollLeft += bounds.right - container.right
    } else if (bounds.top < container.top)
      list.scrollTop -= container.top - bounds.top
    else if (bounds.bottom > container.bottom)
      list.scrollTop += bounds.bottom - container.bottom
  }, [activeIndex, mobile, open])
  useEffect(() => {
    const close = () => setOpen(false)
    Router.events.on('routeChangeStart', close)
    return () => {
      Router.events.off('routeChangeStart', close)
      cancelAnimationFrame(animationFrame.current)
    }
  }, [])
  useEffect(() => {
    if (!open) return undefined
    const { body } = document
    const previous = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    }
    const y = window.scrollY
    const path = window.location.pathname
    body.style.position = 'fixed'
    body.style.top = `-${y}px`
    body.style.width = '100%'
    body.style.overflow = 'hidden'
    return () => {
      Object.assign(body.style, previous)
      if (window.location.pathname === path) window.scrollTo(0, y)
    }
  }, [open])
  const onScroll = () => {
    cancelAnimationFrame(animationFrame.current)
    animationFrame.current = requestAnimationFrame(() => {
      const node = rail.current
      if (node?.clientWidth)
        setIndex(
          clampIndex(
            Math.round(node.scrollLeft / node.clientWidth),
            media.length,
          ),
        )
    })
  }
  const pointerDown = (event) => {
    ignoreClick.current = false
    // Touch uses native horizontal snapping; vertical gestures stay native page scrolling.
    if (
      event.pointerType !== 'mouse' ||
      event.button !== 0 ||
      event.target.closest('video')
    )
      return
    drag.current = { x: event.clientX, start: rail.current.scrollLeft }
  }
  const pointerMove = (event) => {
    if (!drag.current) return
    const dx = event.clientX - drag.current.x
    if (Math.abs(dx) > 8) {
      ignoreClick.current = true
      rail.current.classList.add('pdp-gallery-dragging')
      rail.current.setPointerCapture?.(event.pointerId)
      rail.current.scrollLeft = drag.current.start - dx
      event.preventDefault()
    }
  }
  const pointerEnd = () => {
    if (!drag.current) return
    drag.current = null
    rail.current.classList.remove('pdp-gallery-dragging')
    select(Math.round(rail.current.scrollLeft / rail.current.clientWidth))
  }
  const navigateWithKeyboard = (event) => {
    if (event.target.tagName === 'VIDEO') return
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault()
      const next = clampIndex(
        activeIndex + (event.key === 'ArrowRight' ? 1 : -1),
        media.length,
      )
      select(next)
      if (!open)
        rail.current?.children[next]
          ?.querySelector('button')
          ?.focus({ preventScroll: true })
    }
  }
  const renderThumbnails = (viewer = false) => (
    <div
      className={`pdp-gallery-thumbnails${
        viewer ? ' pdp-gallery-viewer-thumbnails' : ''
      }`}
      ref={viewer ? viewerThumbnails : thumbnails}
      role="group"
      aria-label={labels('gallery')}
    >
      {media.map((item, i) => (
        <button
          type="button"
          key={item.key}
          aria-label={label(item, i)}
          aria-pressed={activeIndex === i}
          onClick={() => select(i)}
        >
          {item.poster && (
            <ResponsiveImage
              src={item.poster}
              alt=""
              imgRatio={1.33}
              imgStyle={{ objectFit: 'contain' }}
            />
          )}
          {item.src && (
            <span className="pdp-gallery-video-badge" aria-hidden="true">
              ▶
            </span>
          )}
        </button>
      ))}
    </div>
  )

  return (
    <div
      ref={root}
      className={`pdp__carousel-images pdp-gallery${
        mobile ? ' pdp-gallery-mobile' : ' pdp-gallery-desktop'
      }`}
    >
      <style jsx>{styles}</style>
      <div className="pdp-gallery-layout">
        <div className="pdp-gallery-main">
          <div
            ref={rail}
            className="pdp-gallery-rail"
            onScroll={onScroll}
            onPointerDown={pointerDown}
            onPointerMove={pointerMove}
            onPointerUp={pointerEnd}
            onPointerCancel={pointerEnd}
            onLostPointerCapture={pointerEnd}
            role="group"
            aria-label={labels('gallery')}
          >
            {media.map((item, i) => (
              <div
                key={item.key}
                className="pdp-gallery-slide"
                aria-hidden={activeIndex !== i}
              >
                {item.src ? (
                  <React.Fragment>
                    {activeIndex === i && visible && !open && (
                      <MediaVideo
                        key={item.key}
                        src={item.src}
                        poster={item.poster}
                        autoPlay={item.autoPlay}
                        label={label(item, i)}
                      />
                    )}
                    {!(activeIndex === i && visible && !open) &&
                      item.poster && (
                        <ResponsiveImage
                          src={item.poster}
                          alt={label(item, i)}
                          imgRatio={1.33}
                          imgStyle={{ objectFit: 'contain' }}
                        />
                      )}
                  </React.Fragment>
                ) : (
                  <button
                    type="button"
                    tabIndex={activeIndex === i ? 0 : -1}
                    onKeyDown={navigateWithKeyboard}
                    aria-label={`${labels('enlarge')}: ${label(item, i)}`}
                    onClick={() => {
                      if (!ignoreClick.current) {
                        select(i)
                        setOpen(true)
                      }
                    }}
                  >
                    <ResponsiveImage
                      src={item.poster}
                      alt={label(item, i)}
                      imgRatio={1.33}
                      imgStyle={{ objectFit: 'contain' }}
                      fetchPriority={i === 0 ? 'high' : undefined}
                      loading={i === 0 ? 'eager' : 'lazy'}
                    />
                  </button>
                )}
              </div>
            ))}
          </div>
          <div
            className={`pdp-gallery-count ${Typo.caption}`}
            aria-live="polite"
          >
            {activeIndex + 1} / {media.length}
          </div>
          <button
            className="pdp-gallery-enlarge"
            type="button"
            aria-label={labels('enlarge')}
            onClick={() => {
              ignoreClick.current = false
              setOpen(true)
            }}
          >
            <span aria-hidden="true">⛶</span>
          </button>
          {!mobile && media.length > 1 && (
            <React.Fragment>
              <button
                className="pdp-gallery-previous"
                type="button"
                aria-label={labels('previous')}
                disabled={activeIndex === 0}
                onClick={() => select(activeIndex - 1)}
              >
                ‹
              </button>
              <button
                className="pdp-gallery-next"
                type="button"
                aria-label={labels('next')}
                disabled={activeIndex === media.length - 1}
                onClick={() => select(activeIndex + 1)}
              >
                ›
              </button>
            </React.Fragment>
          )}
        </div>
        {renderThumbnails()}
      </div>
      <div className="pdp-gallery-services">
        {isShownShopTheLook && (
          <ShopTheLook
            productId={id}
            isPhone={mobile}
            isHiddenShopTheLook={false}
            shopLookCtaEventTracking={shopLookCtaEventTracking}
          />
        )}
        {mobile && virtual_try_on_enabled && (
          <ProductVirtualTryOn
            compact
            productId={id}
            isPhone={mobile}
            isVirtualTryOn={isOpenVirtualTryOn}
          />
        )}
      </div>
      <ReactModal
        isOpen={open}
        appElement={appElement || undefined}
        ariaHideApp={!!appElement}
        contentLabel={labels('gallery')}
        className="pdp-gallery-dialog"
        overlayClassName="pdp-gallery-overlay"
        bodyOpenClassName="pdp-gallery-modal-open"
        onRequestClose={() => setOpen(false)}
        onAfterOpen={() => closeButton.current?.focus({ preventScroll: true })}
        preventScroll
      >
        <div
          className="pdp-gallery-viewer"
          onKeyDown={navigateWithKeyboard}
          role="presentation"
        >
          <header className="pdp-gallery-viewer-header">
            <span className={Typo.body2} aria-live="polite">
              {activeIndex + 1} / {media.length}
            </span>
            <button
              ref={closeButton}
              type="button"
              onClick={() => setOpen(false)}
              aria-label={labels('close')}
            >
              ×
            </button>
          </header>
          {active.src ? (
            <div className="pdp-gallery-viewer-video">
              <MediaVideo
                key={active.key}
                src={active.src}
                poster={active.poster}
                autoPlay={active.autoPlay}
                label={label(active, activeIndex)}
              />
            </div>
          ) : (
            <ZoomImage
              key={active.key}
              src={active.poster}
              alt={label(active, activeIndex)}
              labels={labels}
              onPrevious={() => select(activeIndex - 1)}
              onNext={() => select(activeIndex + 1)}
            />
          )}
          <button
            className="pdp-gallery-viewer-previous"
            type="button"
            aria-label={labels('previous')}
            disabled={activeIndex === 0}
            onClick={() => select(activeIndex - 1)}
          >
            ‹
          </button>
          <button
            className="pdp-gallery-viewer-next"
            type="button"
            aria-label={labels('next')}
            disabled={activeIndex === media.length - 1}
            onClick={() => select(activeIndex + 1)}
          >
            ›
          </button>
          {renderThumbnails(true)}
        </div>
      </ReactModal>
    </div>
  )
}

ProductGallery.defaultProps = {
  isShownShopTheLook: false,
  shopLookCtaEventTracking: null,
  virtual_try_on_enabled: false,
  isOpenVirtualTryOn: false,
}
ProductGallery.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  media: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      poster: PropTypes.string,
      src: PropTypes.string,
      imageType: PropTypes.string,
      autoPlay: PropTypes.bool,
    }),
  ).isRequired,
  mobile: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  isShownShopTheLook: PropTypes.bool,
  shopLookCtaEventTracking: PropTypes.func,
  virtual_try_on_enabled: PropTypes.bool,
  isOpenVirtualTryOn: PropTypes.bool,
}
export default ProductGallery
